/* istanbul ignore file */

import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Signal,
  computed,
  input,
  signal,
  WritableSignal,
  viewChild,
  effect,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup } from '@angular/forms';

import {
  BillingPeriodConfiguration,
  BillingPeriodicity,
  PriceWithTaxCalculation,
} from '@yoimo/interfaces';
import { SubscriptionOption } from '@yoimo/joymo-ui';

import { Subject, filter, takeUntil } from 'rxjs';

import { ScopeService } from '@core/services';

import { OfferInstallmentsComponent } from '../../payments/offer-installments/offer-installments.component';
import {
  AdditionalPaymentConfig,
  PlanOption,
  SubscriptionCardDetails,
  TicketOption,
} from '../ticket-options-types';

const billingLabels = {
  installmentsHeading: $localize`:@@payInInstallmentsDisclaimerHeading:Pay in installments`,
  installmentsDisclaimer: $localize`:@@payInInstallmentsDisclaimerText:Please note that you cannot cancel individual payments until the entire sum has been paid.`,
  periodicity: {
    MONTHLY: $localize`:@@billedMonthly:Billed once a month`,
    YEARLY: $localize`:@@billedYearly:Billed once a year`,
  } as Record<BillingPeriodicity, string>,
} as const;

type TaxItem = {
  amount: number;
  name: string;
  rate: string;
};
type TotalDue = {
  total: number;
  currency: string;
  taxLines?: {
    inclusive?: TaxItem[];
    exclusive?: TaxItem[];
  };
};

/**
 * Displays info about ticket options and allow the user to choose from alternative prices (if available).
 * Works with both single tickets and plans.
 */
@Component({
  selector: 'joymo-ticket-option-selector',
  styleUrls: ['./ticket-option-selector.scss'],
  templateUrl: './ticket-option-selector.component.html',
})
export class TicketOptionSelector implements OnInit, OnDestroy {
  installmentsTpl = viewChild(OfferInstallmentsComponent);
  readonly channel = toSignal(this.scopeService.listenToChannel());

  /** Ticket or plan to be displayed */
  option = input.required<TicketOption | PlanOption>();
  /** Video ID selected for this ticket if present */
  videoId = input<string>();
  /** Allow the parent component to toggle the trial label */
  @Input() hideTrialLabel?: boolean;
  /** Allow the parent component to restrict the use of coupons */
  @Input() disableCoupons?: boolean;
  /** Allow the parent component to restrict selecting of periodicity */
  @Input() disablePeriodicityToggle?: boolean;
  /** Allow the parent component to restrict installments */
  @Input() disableInstallments?: boolean;

  @Output() onActionResult = new EventEmitter<'success' | 'failure'>();

  readonly billingLabels = billingLabels;
  readonly form = new FormGroup({
    coupon: new FormControl<string>('', { nonNullable: true }),
    periodicity: new FormControl<BillingPeriodicity | undefined>(undefined, {
      nonNullable: true,
    }),
    useInstallments: new FormControl<boolean>(false, { nonNullable: true }),
  });

  /** Current ticket, if applicable */
  protected ticket: Signal<TicketOption | undefined>;
  /** Current plan option, if applicable */
  protected plan: Signal<PlanOption | undefined>;
  /** Alternative prices for the current option */
  protected planOptionsPrices: Signal<SubscriptionOption[]>;
  /** Active periodicity config for a given plan */
  protected activePeriodicity: Signal<BillingPeriodConfiguration | null>;
  protected selectedPlanPrice: Signal<
    SubscriptionCardDetails['planPrices'][number] | null
  >;

  protected estimatedTaxCalculation = signal<PriceWithTaxCalculation | null>(
    null
  );

  protected currency: Signal<string>;
  /** Price after a coupon is applied */
  protected discountedPrice: WritableSignal<number | undefined>;
  /** Describe the total payment */
  protected toBePaid: Signal<TotalDue | undefined>;
  /** Total price if paying with installments */
  protected totalInstallmentPrice: Signal<number | undefined>;
  protected isTicketRedemptionLimited: Signal<boolean>;
  protected hideDetails = signal<boolean>(false);

  protected additionalPaymentConfig: Signal<
    AdditionalPaymentConfig | undefined
  >;

  private destroy$ = new Subject<void>();

  constructor(private scopeService: ScopeService) {
    this.plan = computed(() => {
      const option = this.option();
      return option.type === 'PLAN' ? option : undefined;
    });

    this.planOptionsPrices = computed(() => {
      const plan = this.plan();
      return plan ? this.getFormattedPlanPrices(plan) : [];
    });

    this.ticket = computed(() => {
      const option = this.option();
      return option.type === 'TICKET' ? option : undefined;
    });

    this.currency = computed(() => {
      const ticket = this.ticket();
      const plan = this.plan();
      if (ticket?.optionPrice) {
        return ticket.optionPrice.currency;
      }

      if (plan) {
        return plan.planData.priceAlternatives[0].currency;
      }

      throw new Error('No Ticket Or Price defined to calculate currency');
    });

    this.isTicketRedemptionLimited = computed(() => {
      const ticket = this.ticket();
      return !!ticket && !!ticket.ticketData.scope?.redemptionLimit;
    });

    this.discountedPrice = signal<number | undefined>(undefined);

    this.totalInstallmentPrice = computed(() => {
      const installmentsTpl = this.installmentsTpl();
      return installmentsTpl?.totalInstallmentsPrice;
    });

    const periodicityValueChanges = toSignal(
      this.form.controls.periodicity.valueChanges
    );

    // Reset the form values when periodicity changes
    effect(() => {
      const _periodicity = periodicityValueChanges();
      this.form.controls.useInstallments.reset();
      this.form.controls.coupon.reset();
    });

    // Preselect the initial periodicity when plan changes
    effect(() => {
      const plan = this.plan();
      if (plan) {
        // Prefill periodicity
        const fallbackPeriodicity =
          plan.cardDetails.planPrices.find((p) => p.highlighted)?.periodicity ||
          plan.cardDetails.planPrices[0].periodicity;
        this.form.controls.periodicity.setValue(
          plan.selectedPeriodicity || fallbackPeriodicity
        );
      }
    });

    this.activePeriodicity = computed(() => {
      const plan = this.plan();
      const periodicity = periodicityValueChanges();
      if (!plan || !periodicity) {
        return null;
      }
      return this.getActiveBillingPeriodConfig(plan, periodicity);
    });

    this.selectedPlanPrice = computed(() => {
      const plan = this.plan();
      if (!plan) {
        return null;
      }
      const periodicity = periodicityValueChanges();
      if (!periodicity) {
        return null;
      }
      return this.getSelectedPlanPrice(
        plan.cardDetails.planPrices,
        periodicity
      );
    });

    this.additionalPaymentConfig = toSignal(this.form.valueChanges);

    this.toBePaid = computed(() => {
      const ticket = this.ticket();
      const plan = this.plan();
      const formValue = this.additionalPaymentConfig();
      const currency = this.currency();
      const activePeriodicity = this.activePeriodicity();
      const selectedPlanPrice = this.selectedPlanPrice();
      const discountedPrice = this.discountedPrice();
      const taxCalculation = this.estimatedTaxCalculation();

      if (ticket) {
        return this.getTotalPriceForTicket(ticket, currency, taxCalculation);
      }

      if (plan && selectedPlanPrice && activePeriodicity) {
        return this.getTotalPriceForSubscription(
          selectedPlanPrice,
          discountedPrice,
          currency,
          !!formValue?.useInstallments,
          activePeriodicity
        );
      }

      return undefined;
    });
  }

  ngOnInit() {
    if (!this.option()) {
      throw new Error('Ticket or plan not specified');
    }

    this.handleFormChanges();
    this.form.reset();
  }

  handleDiscountedPrice(newPrice: number | undefined) {
    this.form.controls.useInstallments.reset();
    this.discountedPrice.set(newPrice);
  }

  private getSelectedPlanPrice(
    prices: PlanOption['cardDetails']['planPrices'],
    periodicity: BillingPeriodicity
  ) {
    return prices.find((p) => p.periodicity === periodicity) || prices[0];
  }

  private getFormattedPlanPrices(plan: PlanOption): SubscriptionOption[] {
    return plan.cardDetails.planPrices.map((planPrice) => {
      return {
        discount: planPrice.discount || '',
        label: planPrice.name,
        value: planPrice.periodicity,
      };
    });
  }

  private getActiveBillingPeriodConfig(
    plan: PlanOption,
    periodicity: BillingPeriodicity
  ): BillingPeriodConfiguration {
    const config = plan.planData.priceAlternatives[0].billingPeriods.find(
      ({ period }) => period === periodicity
    );

    if (!config) {
      throw new Error(`PlanRequiresBillingConfig:${plan.planData.docId}`);
    }

    return config;
  }

  private getTotalPriceForTicket(
    ticket: TicketOption,
    currency: string,
    taxCalculation: PriceWithTaxCalculation | null
  ): TotalDue {
    if (!ticket.cardDetails.price) {
      throw new Error('No price set');
    }

    const taxLineMapper = (
      ti: PriceWithTaxCalculation['taxItems'][number]
    ): TaxItem => {
      return {
        amount: ti.taxAmount,
        name: ti.type,
        rate:
          'percentageDecimal' in ti.rate
            ? ti.rate.percentageDecimal + '%'
            : ti.rate.flat,
      };
    };

    if (taxCalculation) {
      const inclusive = taxCalculation.taxItems
        .filter((ti) => ti.inclusive)
        .map(taxLineMapper);
      const exclusive = taxCalculation.taxItems
        .filter((ti) => !ti.inclusive)
        .map(taxLineMapper);
      return {
        total: taxCalculation.duePrice,
        currency: taxCalculation.currency,
        taxLines: {
          inclusive,
          exclusive,
        },
      };
    }

    return {
      total: ticket.cardDetails.price.amount,
      currency,
    };
  }

  private getTotalPriceForSubscription(
    selectedPlanPrice: SubscriptionCardDetails['planPrices'][number],
    discountedPrice: number | undefined,
    currency: string,
    useInstallments: boolean,
    activePeriodicity: BillingPeriodConfiguration
  ): TotalDue {
    if (discountedPrice !== undefined) {
      return {
        total: discountedPrice,
        currency,
      };
    }
    if (useInstallments && activePeriodicity.installments) {
      return {
        total: activePeriodicity.installments.price,
        currency,
      };
    }
    if (selectedPlanPrice) {
      return {
        total: selectedPlanPrice.priceRaw.amount,
        currency,
      };
    }
    throw new Error('NotImplemented');
  }

  private handleFormChanges(): void {
    const { useInstallments, coupon } = this.form.controls;

    useInstallments.valueChanges
      .pipe(
        filter((res) => !!res),
        takeUntil(this.destroy$)
      )
      .subscribe((_) => {
        this.form.controls.coupon.reset();
      });

    coupon.valueChanges
      .pipe(
        filter((res) => !!res),
        takeUntil(this.destroy$)
      )
      .subscribe((_) => {
        this.form.controls.useInstallments.reset();
      });

    /*this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.onUserSelection.emit(value);
    });*/
  }

  handleTaxCalculation(
    type: 'FINAL' | 'ESTIMATE',
    calculation: PriceWithTaxCalculation
  ) {
    if (type === 'FINAL') {
      this.hideDetails.set(true);
    } else {
      this.estimatedTaxCalculation.set(calculation);
    }
    /*console.log("Price calculation is", calculation);
    if (this.selectedOption?.type === "TICKET") {
      this.selectedOption= {
        ...this.selectedOption,
        taxCalculation:calculation
      };
    } else if (this.selectedOption?.type === "PLAN") {

    }*/
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
